import { Packets } from '@kaetram/common/network';

import type {
    AbilityCallback,
    AchievementCallback,
    AnimationCallback,
    BlinkCallback,
    BubbleCallback,
    CameraCallback,
    ChatCallback,
    CoinRankingCallback,
    CTFChallengeCallback,
    CombatCallback,
    CommandCallback,
    ContainerCallback,
    CraftingCallback,
    DeathCallback,
    DespawnCallback,
    EffectCallback,
    EnchantCallback,
    EntityListCallback,
    EquipmentCallback,
    ExperienceCallback,
    FriendsCallback,
    GuildCallback,
    HandshakeCallback,
    HealCallback,
    MapCallback,
    MinigameCallback,
    MovementCallback,
    MusicCallback,
    NetworkCallback,
    NotificationCallback,
    NPCCallback,
    OverlayCallback,
    PartyCallback,
    PointerCallback,
    PointsCallback,
    PoisonCallback,
    PVPCallback,
    QuestCallback,
    RankCallback,
    RespawnCallback,
    SkillCallback,
    SpawnCallback,
    StoreCallback,
    SyncCallback,
    TeleportCallback,
    TradeCallback,
    UpdateCallback,
    WelcomeCallback,
    PlayerShopCallback,
    PetAbilityCallback,
    CutsceneCallback
} from '@kaetram/common/types/messages/outgoing';
import type App from '../app';

export default class Messages {
    private messages: (() => ((...data: never[]) => void) | undefined)[] = [];

    private handshakeCallback?: HandshakeCallback;
    private welcomeCallback?: WelcomeCallback;
    private mapCallback?: MapCallback;
    private spawnCallback?: SpawnCallback;
    private equipmentCallback?: EquipmentCallback;
    private entityListCallback?: EntityListCallback;
    private syncCallback?: SyncCallback;
    private movementCallback?: MovementCallback;
    private teleportCallback?: TeleportCallback;
    private despawnCallback?: DespawnCallback;
    private combatCallback?: CombatCallback;
    private animationCallback?: AnimationCallback;
    private pointsCallback?: PointsCallback;
    private networkCallback?: NetworkCallback;
    private chatCallback?: ChatCallback;
    private commandCallback?: CommandCallback;
    private containerCallback?: ContainerCallback;
    private abilityCallback?: AbilityCallback;
    private questCallback?: QuestCallback;
    private achievementCallback?: AchievementCallback;
    private notificationCallback?: NotificationCallback;
    private blinkCallback?: BlinkCallback;
    private healCallback?: HealCallback;
    private experienceCallback?: ExperienceCallback;
    private deathCallback?: DeathCallback;
    private musicCallback?: MusicCallback;
    private npcCallback?: NPCCallback;
    private respawnCallback?: RespawnCallback;
    private tradeCallback?: TradeCallback;
    private enchantCallback?: EnchantCallback;
    private guildCallback?: GuildCallback;
    private pointerCallback?: PointerCallback;
    private pvpCallback?: PVPCallback;
    private poisonCallback?: PoisonCallback;
    private storeCallback?: StoreCallback;
    private overlayCallback?: OverlayCallback;
    private cameraCallback?: CameraCallback;
    private bubbleCallback?: BubbleCallback;
    private skillCallback?: SkillCallback;
    private updateCallback?: UpdateCallback;
    private minigameCallback?: MinigameCallback;
    private effectCallback?: EffectCallback;
    private friendsCallback?: FriendsCallback;
    private rankCallback?: RankCallback;
    private craftingCallback?: CraftingCallback;
    private partyCallback?: PartyCallback;
    private coinRankingCallback?: CoinRankingCallback;
    private ctfChallengeCallback?: CTFChallengeCallback;
    private playerShopCallback?: PlayerShopCallback;
    private petAbilityCallback?: PetAbilityCallback;
    private cutsceneCallback?: CutsceneCallback;

    /**
     * Do not clutter up the Socket class with callbacks,
     * have this class here until a better method arises in my head.
     *
     * This class should not have any complex functionality, its main
     * role is to provide organization for packets and increase readability
     *
     * Please respect the order of the Packets Enum and arrange functions
     * accordingly.
     */
    public constructor(private app: App) {
        this.messages[Packets.Handshake] = () => this.handshakeCallback;
        this.messages[Packets.Welcome] = () => this.welcomeCallback;
        this.messages[Packets.Spawn] = () => this.spawnCallback;
        this.messages[Packets.Equipment] = () => this.equipmentCallback;
        this.messages[Packets.List] = () => this.entityListCallback;
        this.messages[Packets.Sync] = () => this.syncCallback;
        this.messages[Packets.Movement] = () => this.movementCallback;
        this.messages[Packets.Teleport] = () => this.teleportCallback;
        this.messages[Packets.Despawn] = () => this.despawnCallback;
        this.messages[Packets.Combat] = () => this.combatCallback;
        this.messages[Packets.Animation] = () => this.animationCallback;
        this.messages[Packets.Points] = () => this.pointsCallback;
        this.messages[Packets.Network] = () => this.networkCallback;
        this.messages[Packets.Chat] = () => this.chatCallback;
        this.messages[Packets.Command] = () => this.commandCallback;
        this.messages[Packets.Container] = () => this.containerCallback;
        this.messages[Packets.Ability] = () => this.abilityCallback;
        this.messages[Packets.Quest] = () => this.questCallback;
        this.messages[Packets.Achievement] = () => this.achievementCallback;
        this.messages[Packets.Notification] = () => this.notificationCallback;
        this.messages[Packets.Blink] = () => this.blinkCallback;
        this.messages[Packets.Heal] = () => this.healCallback;
        this.messages[Packets.Experience] = () => this.experienceCallback;
        this.messages[Packets.Death] = () => this.deathCallback;
        this.messages[Packets.Music] = () => this.musicCallback;
        this.messages[Packets.NPC] = () => this.npcCallback;
        this.messages[Packets.Respawn] = () => this.respawnCallback;
        this.messages[Packets.Trade] = () => this.tradeCallback;
        this.messages[Packets.Enchant] = () => this.enchantCallback;
        this.messages[Packets.Guild] = () => this.guildCallback;
        this.messages[Packets.Pointer] = () => this.pointerCallback;
        this.messages[Packets.PVP] = () => this.pvpCallback;
        this.messages[Packets.Poison] = () => this.poisonCallback;
        this.messages[Packets.Store] = () => this.storeCallback;
        this.messages[Packets.Map] = () => this.mapCallback;
        this.messages[Packets.Overlay] = () => this.overlayCallback;
        this.messages[Packets.Camera] = () => this.cameraCallback;
        this.messages[Packets.Bubble] = () => this.bubbleCallback;
        this.messages[Packets.Skill] = () => this.skillCallback;
        this.messages[Packets.Update] = () => this.updateCallback;
        this.messages[Packets.Minigame] = () => this.minigameCallback;
        this.messages[Packets.Effect] = () => this.effectCallback;
        this.messages[Packets.Friends] = () => this.friendsCallback;
        this.messages[Packets.Rank] = () => this.rankCallback;
        this.messages[Packets.Crafting] = () => this.craftingCallback;
        this.messages[Packets.Party] = () => this.partyCallback;
        this.messages[Packets.CoinRanking] = () => this.coinRankingCallback;
        this.messages[Packets.CTFChallenge] = () => this.ctfChallengeCallback;
        this.messages[Packets.PlayerShop] = () => this.playerShopCallback;
        this.messages[Packets.PetAbility] = () => this.petAbilityCallback;
        this.messages[Packets.Cutscene] = () => this.cutsceneCallback;
    }

    /**
     * Parses through the data and calls the appropriate callback.
     * @param data Packet data containing packet opcode and data.
     */

    public handleData(data: [Packets, ...never[]]): void {
        let packet = data.shift()!,
            message = this.messages[packet]();

        if (message && typeof message === 'function')
            message.call(this, ...(data as unknown[] as never[]));
    }

    /**
     * Packet data received in an array format calls `handleData`
     * for each iteration of packet data.
     * @param data Packet data array.
     */

    public handleBulkData(data: [Packets, ...never[]][]): void {
        for (let info of data) this.handleData(info);
    }

    /**
     * UTF8 messages handler. These are simple messages that are pure
     * strings. These errors are displayed on the login page.
     * @param message UTF8 message received from the server.
     */

    public handleUTF8(message: string): void {
        this.app.toggleLogin(false);

        switch (message) {
            case 'worldfull': {
                this.app.sendError('¡Los servidores están llenos actualmente!');
                break;
            }

            case 'error': {
                this.app.sendError('¡El servidor respondió con un error!');
                break;
            }

            case 'development': {
                this.app.sendError('El juego está en modo desarrollo actualmente.');
                break;
            }

            case 'disallowed': {
                this.app.sendError('¡El servidor no está aceptando conexiones actualmente!');
                break;
            }

            case 'maintenance': {
                this.app.sendError('El juego está en mantenimiento actualmente.');
                break;
            }

            case 'userexists': {
                this.app.sendError('El nombre de usuario ingresado ya existe.');
                break;
            }

            case 'emailexists': {
                this.app.sendError('El correo electrónico ingresado no está disponible.');
                break;
            }

            case 'invalidinput': {
                this.app.sendError(
                    'Datos inválidos. La contraseña debe tener al menos 8 caracteres y el usuario solo puede tener letras, números y _.'
                );
                break;
            }

            case 'loggedin': {
                this.app.sendError('¡El jugador ya ha iniciado sesión!');
                break;
            }

            case 'invalidlogin': {
                this.app.storage.clearAuthToken();
                this.app.sendError('El nombre de usuario o contraseña son incorrectos.');
                break;
            }

            case 'toofast': {
                this.app.sendError('Estás intentando iniciar sesión demasiado rápido.');
                break;
            }

            case 'timeout': {
                this.app.sendError('Has sido desconectado por inactividad prolongada.');
                break;
            }

            case 'updated': {
                this.app.sendError(
                    'El juego ha sido actualizado. Por favor limpia el caché del navegador.'
                );
                break;
            }

            case 'cheating': {
                this.app.sendError(`Ocurrió un error de sincronización cliente-servidor.`);
                break;
            }

            case 'lost': {
                this.app.sendError('Se ha perdido la conexión con el servidor.');
                break;
            }

            case 'toomany': {
                this.app.sendError('Demasiados dispositivos conectados desde tu dirección IP.');
                break;
            }

            case 'ratelimit': {
                this.app.sendError('Estás enviando paquetes demasiado rápido.');
                break;
            }

            default: {
                this.app.sendError('Ocurrió un error desconocido. Por favor reporta el problema.');
                break;
            }
        }
    }

    /**
     * Packet callbacks.
     */

    public onHandshake(callback: HandshakeCallback): void {
        this.handshakeCallback = callback;
    }

    public onWelcome(callback: WelcomeCallback): void {
        this.welcomeCallback = callback;
    }

    public onSpawn(callback: SpawnCallback): void {
        this.spawnCallback = callback;
    }

    public onEquipment(callback: EquipmentCallback): void {
        this.equipmentCallback = callback;
    }

    public onEntityList(callback: EntityListCallback): void {
        this.entityListCallback = callback;
    }

    public onSync(callback: SyncCallback): void {
        this.syncCallback = callback;
    }

    public onMovement(callback: MovementCallback): void {
        this.movementCallback = callback;
    }

    public onTeleport(callback: TeleportCallback): void {
        this.teleportCallback = callback;
    }

    public onDespawn(callback: DespawnCallback): void {
        this.despawnCallback = callback;
    }

    public onCombat(callback: CombatCallback): void {
        this.combatCallback = callback;
    }

    public onAnimation(callback: AnimationCallback): void {
        this.animationCallback = callback;
    }

    public onPoints(callback: PointsCallback): void {
        this.pointsCallback = callback;
    }

    public onNetwork(callback: NetworkCallback): void {
        this.networkCallback = callback;
    }

    public onChat(callback: ChatCallback): void {
        this.chatCallback = callback;
    }

    public onCommand(callback: CommandCallback): void {
        this.commandCallback = callback;
    }

    public onContainer(callback: ContainerCallback): void {
        this.containerCallback = callback;
    }

    public onAbility(callback: AbilityCallback): void {
        this.abilityCallback = callback;
    }

    public onQuest(callback: QuestCallback): void {
        this.questCallback = callback;
    }

    public onAchievement(callback: AchievementCallback): void {
        this.achievementCallback = callback;
    }

    public onNotification(callback: NotificationCallback): void {
        this.notificationCallback = callback;
    }

    public onBlink(callback: BlinkCallback): void {
        this.blinkCallback = callback;
    }

    public onHeal(callback: HealCallback): void {
        this.healCallback = callback;
    }

    public onExperience(callback: ExperienceCallback): void {
        this.experienceCallback = callback;
    }

    public onDeath(callback: DeathCallback): void {
        this.deathCallback = callback;
    }

    public onMusic(callback: MusicCallback): void {
        this.musicCallback = callback;
    }

    public onNPC(callback: NPCCallback): void {
        this.npcCallback = callback;
    }

    public onRespawn(callback: RespawnCallback): void {
        this.respawnCallback = callback;
    }

    public onTrade(callback: TradeCallback): void {
        this.tradeCallback = callback;
    }

    public onEnchant(callback: EnchantCallback): void {
        this.enchantCallback = callback;
    }

    public onGuild(callback: GuildCallback): void {
        this.guildCallback = callback;
    }

    public onPointer(callback: PointerCallback): void {
        this.pointerCallback = callback;
    }

    public onPVP(callback: PVPCallback): void {
        this.pvpCallback = callback;
    }

    public onPoison(callback: PoisonCallback): void {
        this.poisonCallback = callback;
    }

    public onStore(callback: StoreCallback): void {
        this.storeCallback = callback;
    }

    public onMap(callback: MapCallback): void {
        this.mapCallback = callback;
    }

    public onOverlay(callback: OverlayCallback): void {
        this.overlayCallback = callback;
    }

    public onCamera(callback: CameraCallback): void {
        this.cameraCallback = callback;
    }

    public onBubble(callback: BubbleCallback): void {
        this.bubbleCallback = callback;
    }

    public onSkill(callback: SkillCallback): void {
        this.skillCallback = callback;
    }

    public onUpdate(callback: UpdateCallback): void {
        this.updateCallback = callback;
    }

    public onMinigame(callback: MinigameCallback): void {
        this.minigameCallback = callback;
    }

    public onEffect(callback: EffectCallback): void {
        this.effectCallback = callback;
    }

    public onFriends(callback: FriendsCallback): void {
        this.friendsCallback = callback;
    }

    public onRank(callback: RankCallback): void {
        this.rankCallback = callback;
    }

    public onCrafting(callback: CraftingCallback): void {
        this.craftingCallback = callback;
    }

    public onParty(callback: PartyCallback): void {
        this.partyCallback = callback;
    }

    public onCoinRanking(callback: CoinRankingCallback): void {
        this.coinRankingCallback = callback;
    }

    public onCTFChallenge(callback: CTFChallengeCallback): void {
        this.ctfChallengeCallback = callback;
    }

    public onPlayerShop(callback: PlayerShopCallback): void {
        this.playerShopCallback = callback;
    }

    public onPetAbility(callback: PetAbilityCallback): void {
        this.petAbilityCallback = callback;
    }

    public onCutscene(callback: CutsceneCallback): void {
        this.cutsceneCallback = callback;
    }
}
